import { useContext, useState } from 'react';
import { PlusIcon, Cog6ToothIcon } from '@heroicons/react/24/outline';
import { TrashIcon } from '@heroicons/react/24/solid';
import { nothing } from 'maybeasy';
import { observer } from 'mobx-react';
import { Tooltip } from 'react-tooltip';
import { T, TranslationsContext, translation } from '../../../../../../../Translations';
import { ProductCollectionResource } from '../../Types';
import DisplayTypeDropdown from './DisplayTypeDropdown';
import DeleteSectionModal from '../DeleteSectionModal';
import { useLearningCollection } from '../../LearningCollectionContext';

interface Props {
  productCollection: ProductCollectionResource;
  index: number;
  onOpen: () => void;
  onDelete: () => void;
}

function AddPrograms({ productCollection, index, onOpen, onDelete }: Props) {
  const ts = useContext(TranslationsContext);
  const [isHovered, setIsHovered] = useState(false);
  const {
    handleProductCollectionDisplayTypeChange,
    handleProductCollectionDisplayButtonLabelChange,
  } = useLearningCollection();
  const currentDisplayType = (productCollection.payload.display_type || 'grid') as
    | 'carousel'
    | 'grid'
    | 'list';
  const currentDisplayButtonLabel = productCollection.payload.display_button_label;

  return (
    <DisplayTypeDropdown
      currentDisplayType={currentDisplayType}
      currentDisplayButtonLabel={currentDisplayButtonLabel}
      onDisplayTypeChange={(displayType) =>
        handleProductCollectionDisplayTypeChange(index, displayType)
      }
      onDisplayButtonLabelChange={(buttonLabel) =>
        handleProductCollectionDisplayButtonLabelChange(index, buttonLabel)
      }
      disabled={nothing()}
    >
      {({ onOpen: onOpenDisplayType }) => (
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="px-4 py-2 bg-card text-primary border border-primary rounded transition-all inline-flex items-center gap-2 whitespace-nowrap"
            style={{
              fontSize: 'var(--text-sm)',
              fontWeight: 'var(--font-weight-medium)',
              backgroundColor: isHovered ? 'rgba(24, 122, 144, 0.1)' : undefined,
            }}
            onClick={onOpen}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            aria-haspopup="dialog"
            data-intercom-target="builder-v2-section-add-courses-button"
            data-tooltip-id="add-courses-tooltip"
            data-tooltip-content={translation('Find courses to add')(ts)}
          >
            <PlusIcon className="w-4 h-4" aria-hidden="true" />
            <T kind="Add courses" />
          </button>
          <button
            type="button"
            onClick={onOpenDisplayType}
            className="p-2 hover:bg-muted rounded transition-colors flex-shrink-0"
            aria-label={translation('Section settings')(ts)}
            data-intercom-target="builder-v2-display-type-dropdown"
            data-tooltip-id="section-settings-tooltip"
            data-tooltip-content={translation('Change Section Display')(ts)}
          >
            <Cog6ToothIcon className="w-4 h-4 text-muted-foreground" />
          </button>
          <DeleteSectionModal sectionName={productCollection.payload.name} onConfirm={onDelete}>
            {({ onOpen: onOpenDelete }) => (
              <button
                type="button"
                onClick={onOpenDelete}
                className="p-2 hover:bg-muted rounded transition-colors flex-shrink-0"
                aria-label={translation('Delete this section')(ts)}
                aria-haspopup="dialog"
                data-intercom-target="builder-v2-section-delete-button"
                data-tooltip-id="delete-section-tooltip"
                data-tooltip-content={translation('Remove section')(ts)}
              >
                <TrashIcon className="w-4 h-4 text-muted-foreground" aria-hidden="true" />
              </button>
            )}
          </DeleteSectionModal>
          <Tooltip
            id="add-courses-tooltip"
            place="bottom"
            style={{ zIndex: 9999 }}
            openEvents={{ mouseenter: true }}
            closeEvents={{ mouseleave: true }}
          />
          <Tooltip
            id="section-settings-tooltip"
            place="bottom"
            style={{ zIndex: 9999 }}
            openEvents={{ mouseenter: true }}
            closeEvents={{ mouseleave: true }}
          />
          <Tooltip
            id="delete-section-tooltip"
            place="bottom"
            style={{ zIndex: 9999 }}
            openEvents={{ mouseenter: true }}
            closeEvents={{ mouseleave: true }}
          />
        </div>
      )}
    </DisplayTypeDropdown>
  );
}

export default observer(AddPrograms);
